/**
 * Superagent administration overview — `GET reports/dashboard/`, same query
 * key (`['reports-dashboard']`) `ReportsDashboardPage.tsx` uses so TanStack
 * Query dedupes if a user visits both. Deliberately lighter than the other
 * three home pages: `/admin` is not a superagent's actual default landing
 * route (`homePathForRole` sends them to `/landlord`), and user-management
 * WRITES have no endpoint on either backend yet (see the note on
 * src/layouts/AdminShell.tsx) — so this is a quick headline summary plus a
 * link into user management, not a full dashboard.
 */
import { useQuery } from '@tanstack/react-query';
import { Column, Grid } from '@carbon/react';
import { Building, UserMultiple, Security } from '@carbon/icons-react';
import { parseApiError } from '../../lib/apiError';
import { getDashboard } from '../../api/endpoints/reports';
import { isTenantDashboard } from '../../types/report';
import { StatTile } from '../../components/StatTile';

export function AdminHome(): React.JSX.Element {
  const query = useQuery({
    queryKey: ['reports-dashboard'],
    queryFn: () => getDashboard(),
  });

  if (query.isPending) return <p>Loading…</p>;
  if (query.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(query.error).message}
      </p>
    );
  }

  const dashboard = query.data;
  if (isTenantDashboard(dashboard)) return <></>;

  return (
    <section className="admin-home">
      <h2>Administration</h2>
      <div className="dashboard-stats">
        <Grid withRowGap>
          <Column sm={4} md={4} lg={4}>
            <StatTile
              icon={Building}
              to="/landlord/properties"
              value={dashboard.properties.total}
              label="Properties"
              detail={
                dashboard.properties.pending_approval > 0
                  ? `${dashboard.properties.pending_approval} pending approval`
                  : undefined
              }
            />
          </Column>
          <Column sm={4} md={4} lg={4}>
            <StatTile
              icon={UserMultiple}
              to="/landlord/tenants/leases"
              value={dashboard.leases.active}
              label="Active leases"
            />
          </Column>
          <Column sm={4} md={4} lg={4}>
            <StatTile icon={Security} to="/admin/users" value="→" label="Manage users" />
          </Column>
        </Grid>
      </div>
    </section>
  );
}

export default AdminHome;
